/* eslint-disable no-irregular-whitespace */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  Badge,
  Box,
  Checkbox,
  ColorInput,
  createStyles,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import {
  IconArrowDownBar,
  IconBuilding,
  IconChevronDown,
  IconCreditCard,
  IconEye,
  IconLayout,
  IconLetterCase,
  IconPalette,
  IconPhoto,
} from '@tabler/icons';
import React, { ReactNode, useEffect, useState } from 'react';
import PDFViewer from '../../../Atoms/PDFViewer';
import { DocumentLayout, PdfS } from '../../../Service/PdfS';
import { DataS } from '../../../Service/DataS';
import { TestS } from '../../../TestS';
import { RoutingS } from '../../../Service/RoutingS';
import AutocompleteResourceTypeItem from '../../../Organismns/Resources/table/AutocompleteResourceTypeItem';
import { useBankAccount } from '../../../Service/hooks/useBankAccount';
import BookitupTextInput from '../../../Atoms/inputs/BookitupTextInput';

const useStyles = createStyles((theme) => ({
  workspace: {
    display: 'grid',
    gridTemplateColumns: 'minmax(460px, 1fr) minmax(360px, 410px)',
    gap: 0,
    height: '100vh',
    minHeight: 680,
    minWidth: 0,
    width: '100%',

    [`@media (max-width: 1050px)`]: {
      gridTemplateColumns: '1fr',
      height: 'auto',
      minHeight: 0,
    },
  },
  previewColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
  },
  previewShell: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,

    [`@media (max-width: 1050px)`]: {
      minHeight: 720,
    },
  },
  previewToolbar: {
    minHeight: 64,
    padding: `0 ${theme.spacing.lg}px`,
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
  },
  previewViewport: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: `${theme.spacing.xl}px ${theme.spacing.lg}px`,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : '#eef1f5',
    backgroundImage:
      theme.colorScheme === 'dark'
        ? 'none'
        : 'radial-gradient(circle at 1px 1px, rgba(29, 81, 145, 0.08) 1px, transparent 0)',
    backgroundSize: '20px 20px',
  },
  previewDocument: {
    width: 'fit-content',
    minWidth: 0,
    margin: '0 auto',
    '& .react-pdf__Page': {
      overflow: 'hidden',
      borderRadius: theme.radius.sm,
      boxShadow: theme.colorScheme === 'dark' ? theme.shadows.xl : '0 18px 50px rgba(38, 50, 69, 0.15)',
    },
    '& .react-pdf__Page + .react-pdf__Page': {
      marginTop: theme.spacing.xl,
    },
    '& .react-pdf__Page__canvas': {
      display: 'block',
      maxWidth: '100%',
      border: 'none',
    },
  },
  settingsRail: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,

    [`@media (max-width: 1050px)`]: {
      borderLeft: 0,
      borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
    },
  },
  settingsScroll: {
    flex: 1,
    minHeight: 0,
  },
  settingsContent: {
    padding: theme.spacing.md,
  },
  section: {
    padding: `${theme.spacing.xs}px 0 ${theme.spacing.md}px`,

    '& + &': {
      paddingTop: theme.spacing.xl,
      borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
    },
  },
  sectionIcon: {
    flexShrink: 0,
    color: theme.colorScheme === 'dark' ? theme.colors.blue[1] : theme.colors.blue[3],
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.secondaryBlue[0],
  },
  previewBadge: {
    color: theme.colorScheme === 'dark' ? theme.white : theme.black,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
    border: 0,
  },
}));

const selectChevron = <IconChevronDown size={14} />;

const SettingsSection: React.FC<{
  title: string;
  description: string;
  icon: ReactNode;
  children: ReactNode;
}> = ({ title, description, icon, children }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.section}>
      <Group noWrap align="flex-start" spacing="sm" mb="md">
        <ThemeIcon size={36} radius="md" className={classes.sectionIcon}>
          {icon}
        </ThemeIcon>
        <Box sx={{ minWidth: 0 }}>
          <Text size="sm" weight={800}>
            {title}
          </Text>
          <Text size="xs" color="dimmed" mt={2}>
            {description}
          </Text>
        </Box>
      </Group>
      <Stack spacing="md">{children}</Stack>
    </Box>
  );
};

const DocumentLayoutSettings: React.FC<{
  lastModified?: Date | string;
  logoAvailable: boolean;
  logoSettings?: ReactNode;
  backgroundSettings?: ReactNode;
  workspaceHeader?: ReactNode;
}> = ({ lastModified, logoAvailable, logoSettings, backgroundSettings, workspaceHeader }) => {
  const { classes } = useStyles();
  const { ref: previewRef, width: previewWidth } = useElementSize();
  const [loading, setLoading] = useState(false);
  const { bankAccount, editBankAccount } = useBankAccount();
  const [documentLayout, setDocumentLayout] = useState<DocumentLayout>();
  const {
    logoSize,
    hideLogoOnDocuments,
    infoBoxPlacement,
    contactName,
    showFounderPersonalName,
    pageNumberPosition,
    fontColor,
    backgroundColor,
    backgroundTransparent,
    showBorders,
    logoPosition,
    showLineNumber,
    hideBankAccount,
    customSenderInfo,
    headerFontSize,
    defaultFontSize,
    subtextFontSize,
    profileInfoFontSize,
    includeEpcQrCode,
    includePaypalQrCode,
    showCompanyNameExclusively,
  } = documentLayout ?? {};

  const { paypalUserId } = bankAccount ?? {};

  useEffect(() => {
    RoutingS.changeTitle('Document-Layout');
    setLoading(true);
    PdfS.getDocumentLayout()
      .then((layout) => {
        if (layout) {
          setDocumentLayout(layout);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const patchDocumentLayout = (key: string, value: unknown) => {
    if (documentLayout) {
      PdfS.editDocumentLayout(documentLayout.id, { [key]: value }).then((layout) => {
        if (layout) {
          setDocumentLayout(layout);
        }
      });
    }
  };

  const selectedFontValue = documentLayout ? DataS.fontOptions.find((o) => o.value === documentLayout.font) : null;
  const previewPageWidth = previewWidth > 0 ? Math.max(280, Math.min(650, previewWidth - 48)) : 620;

  if (loading) {
    return (
      <Box pos="relative" sx={{ width: '100%', minHeight: '70vh' }}>
        <LoadingOverlay visible overlayBlur={2} loaderProps={{ size: 'xl' }} />
      </Box>
    );
  }

  if (!documentLayout) return null;

  return (
    <Box className={classes.workspace}>
      <Box className={classes.previewColumn}>
        {workspaceHeader}
        <Paper radius={0} className={classes.previewShell}>
          <Group position="apart" noWrap className={classes.previewToolbar}>
            <Group noWrap spacing="xs">
              <ThemeIcon size={36} radius="md" className={classes.sectionIcon}>
                <IconEye size={19} />
              </ThemeIcon>
              <div>
                <Text size="sm" weight={800}>
                  Live-Vorschau
                </Text>
                <Text size="xs" color="dimmed">
                  Änderungen erscheinen nach dem Speichern automatisch.
                </Text>
              </div>
            </Group>
            <Badge variant="filled" radius="sm" className={classes.previewBadge}>
              DIN A4
            </Badge>
          </Group>

          <Box ref={previewRef} className={classes.previewViewport}>
            <Box className={classes.previewDocument}>
              <PDFViewer
                compact
                pageWidth={previewPageWidth}
                url={`${process.env.REACT_APP_PDF_SERVICE_URL}/documentLayouts/sample`}
                documentLayout={documentLayout}
                lastModified={lastModified}
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      <Paper component="aside" radius={0} className={classes.settingsRail}>
        <ScrollArea className={classes.settingsScroll} offsetScrollbars>
          <Stack spacing="md" className={classes.settingsContent}>
            <SettingsSection
              title="Logo & Hintergründe"
              description="Markenassets für die erste und alle weiteren Seiten"
              icon={<IconPhoto size={19} />}
            >
              {logoSettings}

              {logoAvailable && (
                <>
                  <Divider label="Logo-Platzierung" labelPosition="center" />
                  <Box>
                    <Text size="xs" color="dimmed" mb={6}>
                      Größe
                    </Text>
                    <SegmentedControl
                      fullWidth
                      data={DataS.logoSizeOptions}
                      value={logoSize}
                      onChange={(val) => patchDocumentLayout(PdfS.Layout.LOGO_SIZE, val)}
                      data-testid={TestS.DocumentLayout.CONTROL_LOGO_SIZE}
                    />
                  </Box>
                  <Box>
                    <Text size="xs" color="dimmed" mb={6}>
                      Horizontale Position
                    </Text>
                    <Slider
                      min={-260}
                      max={540}
                      defaultValue={logoPosition}
                      onChangeEnd={(val: number) => patchDocumentLayout(PdfS.Layout.LOGO_POSITION, val)}
                      marks={[
                        { value: -100, label: 'Links' },
                        { value: 140, label: 'Mitte' },
                        { value: 380, label: 'Rechts' },
                      ]}
                      mb="lg"
                      data-testid={TestS.DocumentLayout.SLIDER_LOGO_POSITION}
                    />
                  </Box>
                  <Checkbox
                    mt="sm"
                    label="Logo nur für das Online-Portal verwenden"
                    checked={hideLogoOnDocuments}
                    onChange={(e) => patchDocumentLayout(PdfS.Layout.HIDE_LOGO, e.currentTarget.checked)}
                    data-testid={TestS.DocumentLayout.TOGGLE_LOGO_ONLY_FOR_ONLINE_PORTAL}
                  />
                </>
              )}
              {backgroundSettings}
            </SettingsSection>

            <SettingsSection
              title="Absender & Empfänger"
              description="Welche Profil- und Kundeninformationen sichtbar sind"
              icon={<IconBuilding size={19} />}
            >
              <Select
                label="Position Deiner Profilinfos"
                data={DataS.boxPlacementOptions()}
                value={infoBoxPlacement}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.BOX_PLACEMENT, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={30}
                itemComponent={AutocompleteResourceTypeItem}
              />
              <Box>
                <Text size="xs" color="dimmed" mb={6}>
                  Namensangaben
                </Text>
                <SegmentedControl
                  fullWidth
                  data={infoBoxPlacement ? DataS.getContactNameOptions(infoBoxPlacement) : []}
                  value={contactName}
                  onChange={(val: string) => patchDocumentLayout(PdfS.Layout.CONTACT_NAME, val)}
                  data-testid={TestS.DocumentLayout.CONTROL_CONTACT_NAME_PREFERENCE}
                />
              </Box>
              <Checkbox
                mt="sm"
                checked={showFounderPersonalName}
                label="Inhaber:in namentlich anzeigen"
                onChange={(e) => patchDocumentLayout(PdfS.Layout.SHOW_FOUNDER_NAME, e.currentTarget.checked)}
                data-testid={TestS.DocumentLayout.TOGGLE_SHOW_FOUNDER_PERSONAL_NAME}
              />
              <Checkbox
                checked={hideBankAccount}
                label="Bankverbindung nur auf Rechnungen anzeigen"
                onChange={(e) => patchDocumentLayout(PdfS.Layout.HIDE_BANK_ACCOUNT, e.currentTarget.checked)}
                data-testid={TestS.DocumentLayout.TOGGLE_HIDE_BANK_ACCOUNT}
              />
              <Checkbox
                checked={!showCompanyNameExclusively}
                label="Ansprechpartner bei Firmenrechnungen anzeigen"
                onChange={(e) => patchDocumentLayout('showCompanyNameExclusively', !e.currentTarget.checked)}
              />
            </SettingsSection>

            <SettingsSection
              title="Zahlung"
              description="QR-Codes für schnellere Rechnungszahlungen"
              icon={<IconCreditCard size={19} />}
            >
              <Checkbox
                mt="sm"
                checked={includeEpcQrCode}
                label="SEPA-QR-Code für Banküberweisungen"
                onChange={(e) => patchDocumentLayout('includeEpcQrCode', e.currentTarget.checked)}
              />
              <Checkbox
                checked={includePaypalQrCode}
                label="PayPal-QR-Code anzeigen"
                onChange={(e) => patchDocumentLayout('includePaypalQrCode', e.currentTarget.checked)}
              />
              {includePaypalQrCode && bankAccount && (
                <Box>
                  <BookitupTextInput
                    required
                    label="PayPal Me Alias"
                    defaultValue={paypalUserId ?? ''}
                    onBlurSave={(val) => editBankAccount({ paypalUserId: val })}
                  />
                  <Text size="xs" color="dimmed" mt={5}>
                    Deinen Alias findest oder erstellst Du auf{' '}
                    <Text
                      span
                      component="a"
                      color="blue"
                      href="https://www.paypal.com/paypalme"
                      target="_blank"
                      rel="noreferrer"
                    >
                      PayPal.Me
                    </Text>
                    .
                  </Text>
                </Box>
              )}
            </SettingsSection>

            <SettingsSection
              title="Typografie & Farben"
              description="Schriftbild, Größen und Kontrast Deiner Dokumente"
              icon={<IconPalette size={19} />}
            >
              <Select
                label="Schriftart"
                icon={<IconLetterCase size={18} />}
                data={DataS.fontOptions}
                value={selectedFontValue?.value}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.FONT, val)}
                data-testid={TestS.DocumentLayout.SELECT_FONT}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={34}
              />
              <Select
                label="Dokument-Überschrift"
                description="Zum Beispiel „Angebot“ oder „Vertrag“"
                data={DataS.headerFontSizeOptions}
                value={headerFontSize + ''}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.HEADER_FONT_SIZE, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={30}
                itemComponent={AutocompleteResourceTypeItem}
              />
              <Select
                label="Abschnitts-Überschriften"
                description="Produkte, Absätze und Fragen"
                data={DataS.defaultFontSizeOptions}
                value={defaultFontSize + ''}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.DEFAULT_FONT_SIZE, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={30}
                itemComponent={AutocompleteResourceTypeItem}
              />
              <Select
                label="Fließtext"
                description="Produktbeschreibungen und Vertragstexte"
                data={DataS.subtextFontSizeOptions}
                value={subtextFontSize + ''}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.SUBTEXT_FONT_SIZE, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={30}
                itemComponent={AutocompleteResourceTypeItem}
              />
              {infoBoxPlacement !== 'NONE' && (
                <Select
                  label="Profilinfos"
                  data={DataS.profileInfoFontSizeOptions}
                  value={profileInfoFontSize + ''}
                  onChange={(val) => patchDocumentLayout(PdfS.Layout.PROFILE_INFO_FONT_SIZE, val)}
                  styles={{ rightSection: { pointerEvents: 'none' } }}
                  rightSection={selectChevron}
                  rightSectionWidth={30}
                  itemComponent={AutocompleteResourceTypeItem}
                />
              )}
              <Divider />
              <Checkbox
                label="Transparenter Dokumenthintergrund"
                checked={backgroundTransparent}
                onChange={(e) => patchDocumentLayout(PdfS.Layout.BACKGROUND_TRASPARENT, e.currentTarget.checked)}
                data-testid={TestS.DocumentLayout.TOGGLE_TRANSPARENT_BACKGROUND}
              />
              <Group grow align="flex-start">
                <ColorInput
                  disallowInput
                  value={fontColor}
                  onChangeEnd={(val) => patchDocumentLayout(PdfS.Layout.COLOR, val)}
                  label="Schriftfarbe"
                  format="hex"
                  swatches={DataS.monochromeColors}
                />
                {!backgroundTransparent && (
                  <ColorInput
                    disallowInput
                    value={backgroundColor}
                    onChangeEnd={(val) => patchDocumentLayout(PdfS.Layout.BACKGROUND_COLOR, val)}
                    label="Hintergrund"
                    format="hex"
                    swatches={DataS.monochromeColors}
                  />
                )}
              </Group>
            </SettingsSection>

            <SettingsSection
              title="Seitenlayout"
              description="Abstände, Seitennummern und zusätzliche Details"
              icon={<IconLayout size={19} />}
            >
              <Select
                label="Oberer Abstand ab Seite 2"
                description="In der Vorschau nicht sichtbar"
                icon={<IconArrowDownBar size={18} />}
                data={DataS.documentLayoutMarginTopOptions}
                value={documentLayout.marginTop + ''}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.MARGIN_TOP, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={34}
              />
              <Select
                label="Unterer Abstand"
                icon={<IconArrowDownBar size={18} />}
                data={DataS.documentLayoutBottomMarginPageScopeOptions}
                value={documentLayout.bottomMarginPageScope + ''}
                onChange={(val) => patchDocumentLayout(PdfS.Layout.BOTTOM_MARGIN_PAGE_SCOPE, val)}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={selectChevron}
                rightSectionWidth={34}
              />
              <Box>
                <Text size="xs" color="dimmed" mb={6}>
                  Position der Seitennummer
                </Text>
                <SegmentedControl
                  fullWidth
                  data={DataS.pageNumberPositionOptions}
                  value={pageNumberPosition?.toUpperCase()}
                  onChange={(val: string) => patchDocumentLayout(PdfS.Layout.PAGE_POSITION, val)}
                  data-testid={TestS.DocumentLayout.CONTROL_PAGE_NUMBER_POSITION}
                />
              </Box>
              <Divider />
              <Checkbox
                label="Positionsnummern anzeigen"
                checked={showLineNumber}
                onChange={(e) => patchDocumentLayout(PdfS.Layout.SHOW_LINE_NUMBER, e.currentTarget.checked)}
                data-testid={TestS.DocumentLayout.TOGGLE_POSITION_NUMBERS}
              />
              <Checkbox
                label="Rahmen anzeigen"
                checked={showBorders}
                onChange={(e) => patchDocumentLayout(PdfS.Layout.SHOW_BORDER, e.currentTarget.checked)}
                data-testid={TestS.DocumentLayout.TOGGLE_SHOW_BORDERS}
              />
              <Checkbox
                label="Absenderzeile ausblenden oder individualisieren"
                checked={customSenderInfo !== null && customSenderInfo !== undefined}
                onChange={() =>
                  patchDocumentLayout(
                    PdfS.Layout.CUSTOM_SENDER_INFO,
                    customSenderInfo === null || customSenderInfo === undefined ? '' : null,
                  )
                }
              />
              {customSenderInfo !== null && customSenderInfo !== undefined && (
                <TextInput
                  defaultValue={customSenderInfo}
                  onBlur={(e) => patchDocumentLayout(PdfS.Layout.CUSTOM_SENDER_INFO, e.currentTarget.value)}
                  label="Individuelle Absenderzeile"
                  description="Das Feld darf leer bleiben, um die Zeile auszublenden."
                />
              )}
            </SettingsSection>
          </Stack>
        </ScrollArea>
      </Paper>
    </Box>
  );
};

export default DocumentLayoutSettings;
